import { scaleLinear } from 'd3-scale';
import { ticks as d3Ticks } from 'd3-array';

/**
 * The little arithmetic every chart shares (docs/admin-analytics-brief.md, "How it is built"):
 * a nice top for an axis, positions on a linear scale, and a delta in words. Pure functions so
 * they are tested without a DOM.
 */

/**
 * A round top at or above the largest value, the way d3's `nice` picks it: the tick step comes
 * first ({1, 2, 5} × 10ⁿ for about `count` ticks) and the top is the first step at or above the
 * data, so 2,663 tops out at 3,000 (0, 1k, 2k, 3k) and never at 5,000 with half the plot empty.
 */
export function niceMax(values: readonly number[], count = 4): number {
  const max = Math.max(0, ...values);
  if (max === 0) return 1;
  return scaleLinear().domain([0, max]).nice(count).domain()[1] ?? max;
}

/** What an axis counts: `counts` are whole things (companies, pages), so a tick is never a half. */
export interface TickOptions {
  counts?: boolean | undefined;
}

/**
 * Round tick values from 0 to `top` (d3's ticks: steps of 1, 2 or 5 × 10ⁿ), about `count` of them.
 * With `counts`, only whole numbers: a top of 2 ticks 0, 1, 2, never 0, 0.5, 1, 1.5, 2, which an
 * integer format prints as "0, 1, 1, 2, 2" (round 3, dataviz M4).
 */
export function ticks(top: number, count = 4, options: TickOptions = {}): number[] {
  const all = d3Ticks(0, top, count);
  return options.counts ? all.filter((t) => Number.isInteger(t)) : all;
}

/** One axis of a plot: its round top, its tick values, and where a value sits in pixels. */
export interface PlotScale {
  top: number;
  ticks: number[];
  /** The y of a value, in pixels from the plot's own top edge (larger is lower); clamped to the plot. */
  y: (value: number) => number;
}

/**
 * ONE scale for an axis and everything drawn against it: the ticks and `y` come from the same round
 * top, so a mark of 2,000 always stands exactly on the gridline labelled 2,000. `offset` is the plot's
 * top padding inside its SVG.
 */
export function plotScale(
  values: readonly number[],
  innerH: number,
  offset = 0,
  count = 4,
): PlotScale {
  const top = niceMax(values, count);
  return {
    top,
    ticks: ticks(top, count),
    y: (value) => offset + innerH * (1 - fraction(value, top)),
  };
}

/** 0..1 for a value on a scale from 0 to top; clamped. */
export function fraction(value: number, top: number): number {
  if (top <= 0) return 0;
  return Math.min(1, Math.max(0, value / top));
}

/** The label cut to `max` characters with an ellipsis, so a long name never runs out of its column. */
export function clip(label: string, max: number): string {
  return label.length <= max ? label : `${label.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

/** About 6.5px per character at the charts' 11px type: how wide a run of text will be. */
export function textWidth(text: string): number {
  return text.length * 6.5;
}

export type DeltaTone = 'success' | 'danger' | 'muted';

export interface Delta {
  /** e.g. "+12%", "−8%", "new", "same". */
  text: string;
  tone: DeltaTone;
}

/**
 * The change from `previous` to `current` as a short label. `goodWhen` says which direction is
 * good: 'up' for sign-ups, 'down' for problems.
 */
export function delta(current: number, previous: number, goodWhen: 'up' | 'down' = 'up'): Delta {
  if (previous === 0 && current === 0) return { text: 'same', tone: 'muted' };
  if (previous === 0) return { text: 'new', tone: goodWhen === 'up' ? 'success' : 'danger' };
  const change = Math.round(((current - previous) / previous) * 100);
  if (change === 0) return { text: 'same', tone: 'muted' };
  const up = change > 0;
  const good = up === (goodWhen === 'up');
  return { text: `${up ? '+' : '−'}${Math.abs(change)}%`, tone: good ? 'success' : 'danger' };
}
